import React, { useEffect, useState } from "react";
import {
  getListeFichierClient,
  supprimerDossierPermanent,
  supprimerFichierDossierPermanent,
  supprimerFileMission,
} from "../../../../../data/file";
import { DOWNLOAD_FILE_CLIENT } from "../../../../../api/apiRoutes";
import { ClockCircleOutlined, UserOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Input,
  notification,
  Popconfirm,
  Popover,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import FileUploader from "../../../outils/upload/FileUploader";
import { cleanAndNormalizeName } from "../../../../utilities/Utilities";
import TableTitle from "../../../../common/TableTitle";
import { appTheme } from "../../../../../config/theme";
import { getIconForFileType } from "../../../../layout/menu/CustomMenu";
import { FormContainer, TagItem } from "../../../../common/common.components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudUpload,
  faDownload,
  faExclamationCircle,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import SearchRow from "../../../../common/SearchRow";
import FormLabel from "../../../../common/Form/FormLabel";
import useUsers from "../../../../../hooks/users/useUser";
import { useSelector } from "react-redux";

const DossierPermanentModal = ({ code, client }) => {
  const user = useSelector((state) => state.user);
  const users = useUsers();

  // =========================
  // STATES
  // =========================
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]); // tous les fichiers
  const [filtersItems, setFiltersItems] = useState([]); // fichiers filtrés
  const [isModalOpen, setIsModalOpen] = useState(false);

  // paramètres de recherche
  const [paramsRecherche, setParamsRecherche] = useState({
    infoFile: "",
    user_id: "",
    date_debut: null,
    date_fin: null,
  });

  const [tableParams, setTableParams] = useState({
    pagination: { current: 1, pageSize: 5 },
  });

  // =========================
  // FETCH DOCUMENTS
  // =========================
  useEffect(() => {
    if (code && client) fetchDocuments(client.id, code);
  }, [code, client]);

  const fetchDocuments = (client_id, code) => {
    setLoading(true);
    getListeFichierClient(client_id, code).then((res) => {
      const sorted = res.result.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setFiles(sorted);
      setFiltersItems(sorted);
      setLoading(false);
    });
  };

  // =========================
  // RECHERCHE + RESET
  // =========================
  const handleSearch = () => {
    setLoading(true);
    setTimeout(() => {
      const filtered = files.filter((file) => {
        const matchText =
          file.reference
            ?.toLowerCase()
            .includes(paramsRecherche.infoFile.toLowerCase()) ||
          file.libelle
            ?.toLowerCase()
            .includes(paramsRecherche.infoFile.toLowerCase()) ||
          file.description
            ?.toLowerCase()
            .includes(paramsRecherche.infoFile.toLowerCase());

        const matchUser =
          !paramsRecherche.user_id ||
          file.Lea_User?.id === paramsRecherche.user_id;

        const matchDate =
          (!paramsRecherche.date_debut && !paramsRecherche.date_fin) ||
          moment(file.createdAt).isBetween(
            paramsRecherche.date_debut,
            paramsRecherche.date_fin,
            null,
            "[]"
          );

        return matchText && matchUser && matchDate;
      });
      setFiltersItems(filtered);
      setLoading(false);
    }, 1000);
  };

  const handleReset = () => {
    setLoading(true);
    setParamsRecherche({
      infoFile: "",
      user_id: "",
      date_debut: null,
      date_fin: null,
    });
    setFiltersItems(files);
    setLoading(false);
  };

  // =========================
  // ACTIONS
  // =========================
  const downloadDocumentsClient = (client_bo, client_lea, fichier) => {
    setLoading(true);
    const downloadTimeout = setTimeout(() => {
      const clientBogName = cleanAndNormalizeName(client_bo);
      const clientName = cleanAndNormalizeName(client_lea);
      // ${clientBogName}/${clientName}/${exercice.annee}/${missionName}`

      window.open(
        `${DOWNLOAD_FILE_CLIENT}/${clientBogName}/${clientName}/${fichier}`,
        "_blank"
      );
      setLoading(false);
      notification.success({ description: "Téléchargé avec succès" });
    }, 1500);

    return () => clearTimeout(downloadTimeout);
  };

  const handleDeleteFile = async (file_id) => {
    try {
      const { status, message } = await supprimerFichierDossierPermanent({
        file_id,
      });
      if (status === "success") {
        notification.success({ description: "Fichier supprimé" });
        fetchDocuments(client.id, code);
      } else {
        notification.error({ description: message });
      }
    } catch (err) {
      notification.error({ description: err });
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setFiles([]);
    }
  };

  // =========================
  // TABLE COLUMNS
  // =========================
  const columns = [
    {
      title: () => <TableTitle>#</TableTitle>,
      render: (_, __, index) => index + 1,
      width: 40,
    },
    {
      title: () => <TableTitle>Référence</TableTitle>,
      dataIndex: "reference",
      render: (ref) => <Tag color={appTheme.colors.primary_blue}>{ref}</Tag>,
    },
    {
      title: () => <TableTitle>Document</TableTitle>,
      dataIndex: "libelle",
      render: (_, record) => (
        <div className="d-flex align-items-center">
          {getIconForFileType(record.extension || record.libelle)}
          <span style={{ fontWeight: 700, fontSize: "12px", marginLeft: 8 }}>
            {record.libelle}
          </span>
        </div>
      ),
    },
    {
      title: () => <TableTitle>Taille (KO)</TableTitle>,
      dataIndex: "size",
      render: (size) => <TagItem>{(size / 1024).toFixed(2)}</TagItem>,
    },
    {
      title: () => <TableTitle>Dernière modif.</TableTitle>,
      render: (_, record) => (
        <div>
          <span style={{ fontSize: "11px", fontWeight: 600 }}>
            <UserOutlined /> {record.Lea_User?.nom_user}{" "}
            {record.Lea_User?.prenom_user}
          </span>
          <br />
          <span style={{ fontSize: "11px", fontWeight: 600 }}>
            <ClockCircleOutlined />{" "}
            {moment(record.updatedAt).format("DD/MM/YYYY HH:mm")}
          </span>
        </div>
      ),
    },
    {
      title: () => <TableTitle>Notes</TableTitle>,
      dataIndex: "description",
      render: (desc) =>
        desc && (
          <Popover
            placement="top"
            title="Notes"
            content={<span style={{ fontSize: "13px" }}>{desc}</span>}
          >
            <Button icon={<FontAwesomeIcon icon={faExclamationCircle} />} />
          </Popover>
        ),
    },
    {
      title: () => <TableTitle>Actions</TableTitle>,
      render: (_, record) => (
        <Space>
          {/* Télécharger */}
          <Tooltip title="Télécharger le fichier">
            <Button
              shape="circle"
              style={{ backgroundColor: appTheme.colors.primary }}
              onClick={() =>
                downloadDocumentsClient(
                  record.client_bo,
                  record.client_lea,
                  record.fichier
                )
              }
              icon={<FontAwesomeIcon icon={faDownload} color="white" />}
            />
          </Tooltip>

          {/* Supprimer */}
          {["Admin", "Manager", "Chef de Mission", "Associé"].includes(
            user.libelleProfil
          ) && (
            <Popconfirm
              title="Confirmer la suppression ?"
              onConfirm={() => handleDeleteFile(record.id)}
            >
              <Button
                shape="circle"
                style={{ backgroundColor: "red" }}
                icon={<FontAwesomeIcon icon={faTrash} color="white" />}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <SearchRow
        handleSearch={handleSearch}
        handleReset={handleReset}
        loading={loading}
      >
        <Col
          xs={{ span: 24 }}
          md={{ span: 12 }}
          lg={{ span: 6 }}
          xl={{ span: 6 }}
        >
          <FormLabel label="Fichier" />
          <Input
            allowClear
            style={{ width: "100%" }}
            size="middle"
            placeholder="Reference ou document ou descriptions"
            value={paramsRecherche.infoFile}
            onChange={(e) =>
              setParamsRecherche({
                ...paramsRecherche,
                infoFile: e.target.value,
              })
            }
          />
        </Col>
        <Col
          xs={{ span: 24 }}
          md={{ span: 12 }}
          lg={{ span: 6 }}
          xl={{ span: 6 }}
        >
          <FormLabel label="Utilisateurs" />
          <Select
            allowClear
            placeholder="Utilisateur"
            size="middle"
            style={{ width: "100%" }}
            value={paramsRecherche.user_id}
            onSelect={(value) =>
              setParamsRecherche({ ...paramsRecherche, user_id: value })
            }
            options={
              users &&
              users.users.map((user) => ({
                value: user.id,
                label: `${
                  user.initial || ""
                } - ${user.nom_user.toUpperCase()} ${
                  user.prenom_user ? user.prenom_user.toUpperCase() : ""
                }`,
              }))
            }
          />
        </Col>
        <Col
          xs={{ span: 24 }}
          md={{ span: 12 }}
          lg={{ span: 6 }}
          xl={{ span: 6 }}
        >
          <FormLabel label="Date debut" />
          <DatePicker
            size="middle"
            placeholder="Date début de la création"
            style={{ width: "100%" }}
            allowClear
            onChange={(date, dateString) =>
              date !== null
                ? setParamsRecherche({
                    ...paramsRecherche,
                    date_debut: date.format("YYYY-MM-DD"),
                  })
                : setParamsRecherche({ ...paramsRecherche, date_debut: "" })
            }
            format={["DD-MM-YYYY", "YYYY-MM-DD"]}
          />
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }}>
          <FormLabel label="Date fin" />
          <DatePicker
            size="middle"
            placeholder="Date fin  de la création"
            style={{ width: "100%" }}
            allowClear
            onChange={(date, dateString) =>
              date !== null
                ? setParamsRecherche({
                    ...paramsRecherche,
                    date_fin: date.format("YYYY-MM-DD"),
                  })
                : setParamsRecherche({ ...paramsRecherche, date_fin: "" })
            }
            format="DD-MM-YYYY"
          />
        </Col>
      </SearchRow>

      {/* Tableau des fichiers */}
      <FormContainer>
        <div className="d-flex justify-content-end my-2">
          <Tooltip title="Importer un fichier">
            <Button
              style={{ backgroundColor: appTheme.colors.primary }}
              onClick={() => setIsModalOpen(true)}
              icon={<FontAwesomeIcon icon={faCloudUpload} color="white" />}
            >
              Importer
            </Button>
          </Tooltip>
        </div>

        <Table
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={filtersItems}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={setTableParams}
          size="small"
        />
      </FormContainer>

      {/* Modal d'import */}
      {isModalOpen && (
        <FileUploader
          code={code}
          data={client}
          chargement={loading}
          fetchFile={fetchDocuments}
          visible={isModalOpen}
          //handleVisibility={handleOpenModal}
          handleVisibility={() => setIsModalOpen((prevState) => !prevState)}
        />
      )}
    </>
  );
};

export default DossierPermanentModal;
